import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/data";
import { useBrokers, useInsurers, usePlatforms } from "@/lib/format";
import { TYPES } from "@/components/compare/config";
import { ItemPicker } from "@/components/compare/ItemPicker";
import { Slots } from "@/components/compare/Slots";
import { Verdict, CompareTable, CompareChart } from "@/components/compare/Results";
import { ChooseDialog } from "@/components/compare/ChooseDialog";
import { Glossary } from "@/components/Glossary";

const TypeTabs = ({ type, setType }) => (
  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
    {Object.entries(TYPES).map(([k, t]) => {
      const I = t.icon;
      const on = type === k;
      return (
        <button key={k} data-testid={`compare-type-${k}`} onClick={() => setType(k)}
          className={`flex items-center gap-3 rounded-2xl px-4 py-4 text-left border transition-colors ${on ? "bg-[#10B981] border-[#10B981] text-[#0F172A]" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}`}>
          <I className={`w-5 h-5 shrink-0 ${on ? "" : "text-amber-300"}`} /><span className="font-bold text-sm leading-tight">{t.label}</span>
        </button>
      );
    })}
  </div>
);

export default function ComparePage() {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("type") || "mf";
  const type = TYPES[raw] ? raw : raw === "insurers" ? "life" : "mf";
  const t = TYPES[type];
  const ids = (sp.get("ids") || "").split(",").filter(Boolean);
  const setIds = (next) => setSp({ type, ids: next.join(",") });
  const [chosen, setChosen] = useState(null);
  const { data: mf } = useQuery({ queryKey: ["compare", ids.join(",")], queryFn: () => http.get("/mf/compare", { params: { codes: ids.join(",") } }).then((r) => r.data.items), enabled: !!t.fund && ids.length > 0 });
  const { data: br } = useBrokers();
  const { data: ins } = useInsurers();
  const { data: pl } = usePlatforms(t.platform, !!t.platform);
  const { data: re } = useQuery({ queryKey: ["re-cities"], queryFn: () => http.get("/re/cities").then((r) => r.data) });
  const reCities = (re?.items || []).map((c) => ({ ...c, avg_yield: +(c.localities.reduce((s, l) => s + l.rental_yield, 0) / c.localities.length).toFixed(1) }));
  const pool = t.platform ? pl?.items || [] : type === "brokers" ? br?.items || [] : type === "realestate" ? reCities : (ins?.items || []).filter((x) => x.type === t.insurer);
  const items = t.fund ? (ids.length ? (mf || []).filter((x) => ids.includes(x.code)) : []) : ids.map((id) => pool.find((x) => x.id === id)).filter(Boolean);
  const add = (id) => !ids.includes(id) && ids.length < 3 && setIds([...ids, id]);

  return (
    <div data-testid="compare-page">
      <section className="relative grain overflow-hidden bg-[#1E293B] text-white">
        <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full bg-amber-400/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">Compare · Decide · Proceed</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 max-w-3xl">Compare up to 3 options. <span className="gold-text">Pick the winner.</span></h1>
          <p className="text-slate-300 mt-3 max-w-2xl text-base md:text-lg">1. Choose what to compare  ·  2. Add options  ·  3. See who wins on each point  ·  4. Go ahead with your choice.</p>
          <TypeTabs type={type} setType={(k) => setSp({ type: k, ids: "" })} />
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {ids.length < 3 && (
          <div data-testid="compare-quick-add" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <p className="font-display font-bold text-lg mb-4">Add {t.article || "a"} {t.noun} <span className="text-sm font-normal text-slate-500">({ids.length}/3 added)</span></p>
            <ItemPicker key={type} type={type} pool={pool} ids={ids} onPick={add} />
          </div>
        )}
        <Slots items={items} type={type} pool={pool} ids={ids} setIds={setIds} onChoose={setChosen} />
        {items.length > 0 && (
          <>
            <Verdict items={items} type={type} />
            <CompareTable items={items} type={type} />
            <CompareChart items={items} type={type} />
            <div data-testid="compare-decide" className="rounded-3xl bg-gradient-to-r from-[#1E293B] to-[#0F172A] text-white p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
              <div><p className="font-display text-2xl font-extrabold">Made up your mind?</p><p className="text-slate-300 text-sm mt-1">Go ahead directly with the provider, or get a free expert callback.</p></div>
              <div className="flex flex-wrap gap-3">
                {items.map((x) => <button key={x.code || x.id} data-testid={`compare-decide-${x.code || x.id}`} onClick={() => setChosen(x)} className="h-11 px-5 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold text-sm transition-colors">{t.cta || "Invest in"} {x.name.split(" ").slice(0, 3).join(" ")}</button>)}
              </div>
            </div>
          </>
        )}
        <Glossary set={t.glossary} />
      </div>
      <ChooseDialog item={chosen} isFund={!!t.fund} onClose={() => setChosen(null)} />
    </div>
  );
}
